import { FC, Fragment } from 'react';
import { Link } from '@carbon/react';
import type { PackageSeller } from 'types/api-responses';

interface Props {
  sellers: PackageSeller[];
  /** Rendered when the package has no sellers. */
  empty?: string;
}

/**
 * Comma-joined seller names; a seller that resolved to an entity links to it
 * (opening in a new tab), one that didn't renders as plain text. A legacy
 * package's seller shows its CRM account's name either way — linked when the
 * account resolves to exactly one entity, plain text when it resolves to none
 * or to several. Shared by the packages list, the wizard's source-package
 * table, the package show page and the CRM tract's related-packages table —
 * a plain `href` link so it needs no Router context.
 *
 * @param {Props} props - The sellers and the empty-state text
 * @return {JSX.Element} Linked / plain seller names, or the empty text
 */
export const SellersCell: FC<Props> = ({ sellers, empty = '--' }) => {
  if (sellers.length === 0) return <>{empty}</>;

  return (
    <>
      {sellers.map((seller, idx) => (
        <Fragment key={seller.id ?? `legacy-${idx}`}>
          {idx > 0 && ', '}
          {seller.href ? (
            <Link href={seller.href} target="_blank" rel="noopener noreferrer">
              {seller.name}
            </Link>
          ) : (
            <span>{seller.name}</span>
          )}
        </Fragment>
      ))}
    </>
  );
};
